import {Injectable} from '@angular/core';
import {BehaviorSubject, defer, EMPTY, Observable, of, ReplaySubject, Subject, throwError} from 'rxjs';
import {catchError, concatMap, finalize, map, switchMap, take, tap} from 'rxjs/operators';
import {SettingsInterface} from '../../api/interfaces/settings.interface';
import {UserApiModel} from '../../api/models/user.api-model';
import {UserRestService} from '../../api/services/user.rest.service';
import {TokenDataProviderService} from './token-data-provider.service';

interface SettingsWrite {
  accountId: number;
  update: (settings: SettingsInterface) => SettingsInterface;
  result: ReplaySubject<UserApiModel>;
}

/** Serializes all settings writes, including tutorial progress, against confirmed state. */
@Injectable({providedIn: 'root'})
export class UserSettingsWriterService {
  private readonly writes = new Subject<SettingsWrite>();
  private readonly pending = new BehaviorSubject<number>(0);
  public readonly saving$ = this.pending.pipe(map(count => count > 0));

  constructor(private readonly users: UserRestService, private readonly tokens: TokenDataProviderService) {
    this.writes.pipe(concatMap(write => this.tokens.user$.pipe(
      take(1),
      switchMap(current => {
        if (!write.accountId || current.id !== write.accountId || !current['@id']) {
          return throwError(() => new Error('Settings session changed'));
        }
        const payload = {settings: write.update(current.settings || {})} as UserApiModel;
        return this.users.putSettings(current['@id'], payload);
      }),
      tap(response => {
        // Preserve hydrated media and independent profile edits made during the request.
        this.tokens.user$.pipe(take(1)).subscribe(current => {
          if (current.id === write.accountId) {
            current.settings = response.settings;
            this.tokens.currentUser = of(current);
          }
        });
        write.result.next(response);
        write.result.complete();
      }),
      catchError(error => {
        write.result.error(error);
        return EMPTY;
      }),
      finalize(() => this.pending.next(this.pending.value - 1)),
    ))).subscribe();
  }

  public update(update: SettingsWrite['update']): Observable<UserApiModel> {
    return defer(() => this.tokens.user$.pipe(
      take(1),
      switchMap(current => {
        const result = new ReplaySubject<UserApiModel>(1);
        this.pending.next(this.pending.value + 1);
        this.writes.next({accountId: current.id, update, result});
        return result.asObservable();
      }),
    ));
  }

  public patch(patch: SettingsInterface): Observable<UserApiModel> {
    return this.update(settings => ({
      ...settings,
      ...patch,
      ...(patch.appearance ? {appearance: {...settings.appearance, ...patch.appearance}} : {}),
      privacy: {...settings.privacy, ...patch.privacy},
      notification: {...settings.notification, ...patch.notification},
    }));
  }
}
